'use client';

import React from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { RichTextFragment } from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import Button from '@/ui/components/shared/Button';
import { RichTextRenderer } from '@/ui/components/tileRenderers/RichTextRenderer';

export type WithdrawalConfirmDialogTexts = {
  headline?: string | null;
  text?: RichTextFragment;
  confirmLabel?: string | null;
  cancelLabel?: string | null;
};

type WithdrawalConfirmDialogProps = Readonly<{
  open: boolean;
  texts: WithdrawalConfirmDialogTexts;
  onConfirm: () => void;
  onClose: () => void;
}>;

export function WithdrawalConfirmDialog({
  open,
  texts,
  onConfirm,
  onClose,
}: WithdrawalConfirmDialogProps) {
  return (
    <Dialog open={open} className="relative z-50" onClose={onClose}>
      <div className="fixed inset-0 bg-neutral-100/50" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="relative w-full max-w-lg rounded-2xl bg-white px-6 pb-10 pt-8 shadow-xl sm:px-10">
          <button
            type="button"
            onClick={onClose}
            className="absolute right-4 top-4 rounded-xl p-1 duration-200 ease-in hover:bg-neutral-10"
            aria-label={texts.cancelLabel ?? undefined}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
          <div className="flex flex-col items-center text-center">
            <Dialog.Title as="h3" className="mb-3 text-xl font-bold text-neutral-100">
              {texts.headline}
            </Dialog.Title>
            <div className="mb-8 max-w-sm text-sm text-neutral-60">
              <RichTextRenderer blocks={texts.text} />
            </div>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Button variant="secondary" onClick={onClose}>
                {texts.cancelLabel}
              </Button>
              <Button variant="primary" onClick={onConfirm}>
                {texts.confirmLabel}
              </Button>
            </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
